import { isNil, omit, omitBy } from 'lodash'
import moment from 'moment'

import { UploadedAttachment } from '../../../attachments/types/UploadedAttachment'
import { AosSeverity } from '../../../common/types/AosSeverity'
import {
    AnimalsTaskFormPayload,
    DeIcingTaskFormPayload,
    InspectionTaskFormPayload,
    MaintenanceTaskFormPayload,
    RefuellingTaskFormPayload,
    RescueServiceTaskFormPayload,
    SimpleTaskFormPayload,
    TaskFormPayload,
    TerminalTaskFormPayload,
} from '../payload'
import { initialSimpleTaskPayload } from '../payload'
import { initialPayloadForCategory } from '../payload/TaskFormPayload'
import { TaskCategory } from '../TaskCategory'
import { TaskProcessType } from '../TaskProcessType'
import { TaskStatus } from '../TaskStatus'
import { TaskType } from '../TaskType'
import { BaseTask } from './BaseTask'
import { Task } from './Task'
import { TaskAssignee } from './TaskAssignee'

export type TaskTemplateListItem = {
    id: number
    name: string
    processType: TaskProcessType
    category: TaskCategory
    severity: AosSeverity
    assignees: TaskAssignee[]
    hasLocation: boolean
    attachmentsLength: number
    taskType: TaskType
}

type TemplateOmitted =
    | 'parentTaskId'
    | 'instanceId'
    | 'startTime'
    | 'endTime'
    | 'spendTimeInMinutes'
    | 'status'
    | 'realEndTime'
    | 'lastComment'
    | 'overdue'
    | 'recurrence'
    | 'log'

type TemplateBaseRequired = Omit<BaseTask, TemplateOmitted>

type OptionalDetails<T extends BaseTask> = Partial<Omit<T, keyof BaseTask>>

export type TaskTemplate<T extends BaseTask = Task> = TemplateBaseRequired &
    OptionalDetails<T> & {
        templateName: string
        siteLocation: string
        id: number
    }

export type TaskTemplateDto<T = TaskFormPayload> = Omit<
    T,
    | 'parentTaskId'
    | 'instanceId'
    | 'startTime'
    | 'endTime'
    | 'spendTimeInMinutes'
    | 'status'
    | 'realEndTime'
    | 'lastComment'
    | 'overdue'
    | 'recurrence'
    | 'log'
> & {
    attachments?: UploadedAttachment[]
    siteLocation?: string
    templateName: string
}

export const isTaskTemplate = (t: Task | TaskTemplate): t is TaskTemplate => {
    return !!(t as TaskTemplate).templateName
}

const taskTemplateOmittedKeys = [
    'parentTaskId',
    'instanceId',
    'startTime',
    'endTime',
    'spendTimeInMinutes',
    'status',
    'realEndTime',
    'lastComment',
    'overdue',
    'recurrence',
    'log',
] as const

export type TaskTemplateFormPayload =
    | TaskTemplateDto<SimpleTaskFormPayload>
    | TaskTemplateDto<AnimalsTaskFormPayload>
    | TaskTemplateDto<DeIcingTaskFormPayload>
    | TaskTemplateDto<MaintenanceTaskFormPayload>
    | TaskTemplateDto<InspectionTaskFormPayload>
    | TaskTemplateDto<RescueServiceTaskFormPayload>
    | TaskTemplateDto<RefuellingTaskFormPayload>
    | TaskTemplateDto<TerminalTaskFormPayload>

export type TaskTemplatePayload = TaskTemplateDto<TaskFormPayload>

export const isTaskTemplateDto = (
    t: TaskFormPayload | TaskTemplateFormPayload,
): t is TaskTemplateFormPayload => {
    return !(t as TaskFormPayload).status
}

export const isTaskTemplateFormPayload = (
    t: TaskFormPayload | TaskTemplateFormPayload,
): t is TaskTemplateFormPayload => isTaskTemplateDto(t)

export const initialTemplateForCategory = (category?: TaskCategory): TaskTemplateFormPayload =>
    omit(
        category ? initialPayloadForCategory[category] : initialSimpleTaskPayload,
        taskTemplateOmittedKeys as readonly string[],
    ) as TaskTemplateFormPayload

export const initialTaskTemplate: TaskTemplateFormPayload =
    initialTemplateForCategory() as TaskTemplateDto<SimpleTaskFormPayload>

export const mapTemplateToDto = (t: TaskTemplate): TaskTemplateFormPayload =>
    ({
        ...initialTemplateForCategory(t.category),
        ...omitBy(
            omit(t, ['createdAt', 'createdBy', 'modifiedAt', 'modifiedBy', 'checklists']),
            isNil,
        ),
        checklistsTemplateIds: t.checklists?.map(checklist => checklist.id),
    }) as TaskTemplateFormPayload

type TaskByType<TType extends Task['type']> = Extract<Task, { type: TType }>

export type AnyTaskTemplate = { [K in Task['type']]: TaskTemplate<TaskByType<K>> }[Task['type']]

export type TaskOrTemplatePayload = TaskFormPayload | TaskTemplateFormPayload

export const taskTemplateToTaskFormPayload = (template: TaskTemplate): TaskFormPayload => {
    const categoryInitial = template.category
        ? initialPayloadForCategory[template.category]
        : initialSimpleTaskPayload

    return {
        ...categoryInitial,
        status: TaskStatus.Todo,
        startTime: moment(),
        endTime: moment().add(1, 'hours'),
        ...omitBy(
            omit(template, [
                'templateName',
                'id',
                'checklists',
                'createdAt',
                'createdBy',
                'modifiedAt',
                'modifiedBy',
            ]),
            isNil,
        ),
        checklistsTemplateIds: template.checklists?.map(checklist => checklist.id),
    } as TaskFormPayload
}
